import extend from "./extend";

function slice(x) {
  return Array.prototype.slice.call(x);
}

function join(x) {
  return slice(x).join("");
}

export default function createEnvironment() {
  // Unique variable id counter
  let varCounter = 0;

  // Linked values are passed from this scope into the generated code block
  // Calling link() passes a value into the generated scope and returns
  // the variable name which it is bound to
  const linkedNames = [];
  const linkedValues = [];
  function link(value) {
    for (let i = 0; i < linkedValues.length; ++i) {
      if (linkedValues[i] === value) {
        return linkedNames[i];
      }
    }

    const name = "g" + varCounter++;
    linkedNames.push(name);
    linkedValues.push(value);
    return name;
  }

  // create a code block
  function block() {
    const code = [];
    function push() {
      code.push.apply(code, slice(arguments));
    }

    const vars = [];
    function def() {
      const name = "v" + varCounter++;
      vars.push(name);

      if (arguments.length > 0) {
        code.push(name, "=");
        code.push.apply(code, slice(arguments));
        code.push(";");
      }

      return name;
    }

    return extend(push, {
      def: def,
      toString: function() {
        return join([
          vars.length > 0 ? "var " + vars.join(",") + ";" : "",
          join(code),
        ]);
      },
    });
  }

  function scope() {
    const entry = block();
    const exit = block();

    const entryToString = entry.toString;
    const exitToString = exit.toString;

    function save(object, prop) {
      exit(object, prop, "=", entry.def(object, prop), ";");
    }

    return extend(
      function() {
        entry.apply(entry, slice(arguments));
      },
      {
        def: entry.def,
        entry: entry,
        exit: exit,
        save: save,
        set: function(object, prop, value) {
          save(object, prop);
          entry(object, prop, "=", value, ";");
        },
        toString: function() {
          return entryToString() + exitToString();
        },
      }
    );
  }

  function conditional() {
    const pred = join(arguments);
    const thenBlock = scope();
    const elseBlock = scope();

    const thenToString = thenBlock.toString;
    const elseToString = elseBlock.toString;

    return extend(thenBlock, {
      then: function() {
        thenBlock.apply(thenBlock, slice(arguments));
        return this;
      },
      else: function() {
        elseBlock.apply(elseBlock, slice(arguments));
        return this;
      },
      toString: function() {
        let elseClause = elseToString();
        if (elseClause) {
          elseClause = "else{" + elseClause + "}";
        }
        return join(["if(", pred, "){", thenToString(), "}", elseClause]);
      },
    });
  }

  // procedure list
  const globalBlock = block();
  const procedures = {};
  function proc(name, count) {
    const args = [];
    function arg() {
      const name = "a" + args.length;
      args.push(name);
      return name;
    }

    count = count || 0;
    for (let i = 0; i < count; ++i) {
      arg();
    }

    const body = scope();
    const bodyToString = body.toString;

    const result = (procedures[name] = extend(body, {
      arg: arg,
      toString: function() {
        return join(["function(", args.join(), "){", bodyToString(), "}"]);
      },
    }));

    return result;
  }

  function compile() {
    const code = ['"use strict";', globalBlock, "return {"];
    Object.keys(procedures).forEach(function(name) {
      code.push('"', name, '":', procedures[name].toString(), ",");
    });
    code.push("}");
    const src = join(code)
      .replace(/;/g, ";\n")
      .replace(/}/g, "}\n")
      .replace(/{/g, "{\n");
    const proc = Function.apply(null, linkedNames.concat(src));
    return proc.apply(null, linkedValues);
  }

  return {
    global: globalBlock,
    link: link,
    block: block,
    proc: proc,
    scope: scope,
    cond: conditional,
    compile: compile,
  };
}
